import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { normalizeCountryRiskRatings } from "../helpers/countryRiskRating";
import { AML_RISK_LEVELS } from "../helpers/amlRiskLevel";
import {
  ASSESSEE_TYPES,
  BUSINESS_TYPES,
  CLIENT_STATUSES,
  COUNTRY_RISK_OPTIONS,
  DUAL_FACTORS,
  FACTORS_15A,
  FACTORS_15B,
  MONETARY_ROWS,
  PAPER_NOTES,
  PREVIOUS_RISK_OPTIONS,
  REVIEW_TYPES,
  SCREENED_PARTIES,
  TRUE_HIT_TYPES,
  UNACCEPTABLE_FACTORS,
  assessmentFieldErrors,
  assessmentRoute,
  buildRelatedInformation,
  chooseFinalRisk,
  createEmptyAssessment,
  latestOtherAml,
  withLastAmlAssessmentDate,
  withSuggestion,
} from "../helpers/fwdAmlAssessment";

export const FORM_MAX_WIDTH = 1120;

const RATING_HELP = {
  "Unacceptable Risk":
    "Establishing business or continue business are prohibited. Report to Business Monitoring and Head of FWDFP.",
  "Increased Risk":
    'Hits at least 1 "Risk Factor" in #15 or #16, or 2 or more "Risk Factors" from #17 - #21. EDD and ongoing monitoring are mandatory.',
  "Standard Risk": 'Hits only 1 "Risk Factor" from #17 - #21.',
  "Low Risk": "Does not fall within any of the above higher risk scenarios.",
  "Exempted Risk": "Applying GI and / or MPF business in FWDFP only.",
};

const BUSINESS_HINTS = {
  accountOpening: "Go to #15(a).",
  life: "Go to #15(a) and #16.",
  investment: "Go to #15(a), #15(b) and #16.",
  others: "Go to #15(a).",
  fundSwitching: "Go to Section 6.",
  generalInsurance: "Exempted Risk, go to Section 6.",
  mpf: "Exempted Risk, go to Section 6.",
};

const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const FieldError = ({ message }) =>
  message ? (
    <Text size="xs" color="red" mt={4}>
      {message}
    </Text>
  ) : null;

const RelatedPanel = ({ items }) => {
  const { t } = useTranslation();
  return (
    <Paper
      p="md"
      radius="sm"
      sx={(theme) => ({
        backgroundColor:
          theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
        position: "sticky",
        top: 12,
        maxHeight: 480,
        overflow: "auto",
      })}
    >
      <Text size="xs" weight={700} transform="uppercase" color="dimmed" mb="sm">
        {t("Related information")}
      </Text>
      <Stack spacing="sm">
        {(items || []).map((item) => (
          <Box key={item.label}>
            <Text size="xs" color="dimmed">
              {t(item.label)}
            </Text>
            <Text size="sm" sx={{ whiteSpace: "pre-wrap" }}>
              {item.value}
            </Text>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const SectionBlock = ({ title, open, onToggle, related, children }) => {
  const { t } = useTranslation();
  return (
    <Box mb={28}>
      <UnstyledButton onClick={onToggle} sx={{ width: "100%" }} mb="sm">
        <Text weight={700}>{`${open ? "▾" : "▸"} ${t(title)}`}</Text>
      </UnstyledButton>
      <Collapse in={open}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 320px",
            gap: 24,
            alignItems: "start",
            "@media (max-width: 1100px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
          <Paper withBorder p="md" radius="sm" sx={{ minWidth: 0 }}>
            {children}
          </Paper>
          <RelatedPanel items={related} />
        </Box>
      </Collapse>
    </Box>
  );
};

const ChoiceTable = ({ rows, answers, onChange, disabled, error }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 72px 72px 72px",
          columnGap: 8,
        }}
      >
        <Text size="xs" color="dimmed" />
        {["Yes", "No", "N/A"].map((label) => (
          <Text key={label} size="xs" color="dimmed">
            {t(label)}
          </Text>
        ))}
        {rows.map((row) => (
          <React.Fragment key={row.id}>
            <Text size="sm" py={8} sx={{ borderTop: "1px solid #e9ecef" }}>
              {row.number ? `${row.number}. ` : ""}
              {t(row.label)}
            </Text>
            {["yes", "no", "na"].map((option) => {
              const allowed = option !== "na" || row.na;
              const value = answers?.[row.id] || "";
              return (
                <Box key={option} py={8} sx={{ borderTop: "1px solid #e9ecef" }}>
                  {allowed ? (
                    <Checkbox
                      aria-label={`${row.id}-${option}`}
                      checked={value === option}
                      disabled={disabled}
                      onChange={() => onChange(row.id, value === option ? "" : option)}
                    />
                  ) : null}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <FieldError message={error} />
    </Box>
  );
};

const CheckList = ({ rows, isChecked, onToggle, disabled, detail, onDetail, detailError }) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="xs">
      {rows.map((row) => (
        <Box key={row.id}>
          <Checkbox
            label={t(row.hint ? `${row.label} — ${row.hint}` : row.label)}
            checked={isChecked(row.id)}
            disabled={disabled}
            onChange={() => onToggle(row.id)}
          />
          {row.detail && isChecked(row.id) ? (
            <TextInput
              mt={6}
              ml={28}
              value={detail || ""}
              disabled={disabled}
              error={detailError}
              onChange={(event) => onDetail(event.currentTarget.value)}
            />
          ) : null}
        </Box>
      ))}
    </Stack>
  );
};

const FwdAmlAssessmentForm = ({
  form,
  value,
  onChange,
  error,
  readOnly = false,
  disabled = false,
  getClient,
  getUser,
  getAmlRows,
}) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [countryRatings, setCountryRatings] = useState(() => normalizeCountryRiskRatings());
  const [relatedPartyLists, setRelatedPartyLists] = useState({ persons: [], corporates: [] });
  const locked = Boolean(readOnly || disabled);
  const assessment = withSuggestion(value || {});
  const route = assessmentRoute(assessment);
  const errors = error ? assessmentFieldErrors(assessment) : {};
  const [open, setOpen] = useState(route.sectionOpen);
  const routeKey = [
    route.reason,
    route.show15a,
    route.show15b,
    route.show16,
    route.showSection5,
    route.showQuestion11,
  ].join("|");
  const routeKeyRef = useRef(routeKey);
  const seededRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .search({ apiEntity: "setting", pageSize: 1 })
      .then((result) => {
        if (cancelled) return;
        const setting = result?.docs?.[0] || {};
        setCountryRatings(normalizeCountryRiskRatings(setting.countryRiskRatings));
        setRelatedPartyLists({
          persons: setting.fwdRelatedPersons || [],
          corporates: setting.fwdRelatedCorporates || [],
        });
      })
      .catch((error) => console.log(error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (routeKeyRef.current === routeKey) return;
    routeKeyRef.current = routeKey;
    setOpen(assessmentRoute(assessment).sectionOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  useEffect(() => {
    if (seededRef.current || locked) return;
    seededRef.current = true;
    const clientRecord = getClient?.() || {};
    const blank = createEmptyAssessment(clientRecord, getUser?.() || {});
    const next = { ...blank, ...value };
    ["assessorName", "advisorName", "assesseeName", "assesseeCode", "assesseeType"].forEach(
      (key) => {
        if (!String(value?.[key] || "").trim() && blank[key]) next[key] = blank[key];
      }
    );
    const seeded = withLastAmlAssessmentDate(
      next,
      getAmlRows?.() || clientRecord.aml,
      form?.values
    );
    const synced = withSuggestion(seeded);
    const same = [
      "assessorName",
      "advisorName",
      "assesseeName",
      "assesseeCode",
      "assesseeType",
      "previousAssessmentDate",
      "suggestedRisk",
      "finalRisk",
      "riskOverridden",
    ].every((key) => synced[key] === value?.[key]);
    if (!same) {
      form?.setFieldValue?.("assessment", synced);
      form?.setFieldValue?.("riskLevel", synced.finalRisk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = getClient?.() || {};
  const amlRows = getAmlRows?.() || client.aml;
  const related = useMemo(
    () =>
      buildRelatedInformation(client, {
        assessment,
        previousAml: latestOtherAml(amlRows, form?.values),
        countryRatings,
        relatedPartyLists,
      }),
    // client is a live record; rebuild when its identity fields or screening change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, assessment, amlRows, countryRatings, relatedPartyLists, form?.values?._id]
  );

  const commit = (patch) => {
    const next = withSuggestion({ ...assessment, ...patch });
    onChange(next);
    form?.setFieldValue?.("riskLevel", next.finalRisk);
  };

  const party = (id) =>
    (assessment.screenedParties || []).find((item) => item.id === id) || {
      id,
      checked: false,
      detail: "",
    };

  const setAnswer = (group, id, answer) => {
    commit({ [group]: { ...(assessment[group] || {}), [id]: answer } });
  };

  const toggleIn = (key, id) => {
    const current = assessment[key] || [];
    commit({
      [key]: current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    });
  };

  return (
    <Box sx={{ width: "100%", maxWidth: FORM_MAX_WIDTH }}>
      {route.reason ? (
        <Alert color={route.hardStop === "unacceptable" ? "red" : "blue"} mb="lg">
          {t(route.reason)}
        </Alert>
      ) : null}

      <SectionBlock
        title="Section 1 – Basic Information"
        open={open["1"]}
        onToggle={() => setOpen((current) => ({ ...current, 1: !current["1"] }))}
        related={related.section1}
      >
        <Stack spacing="sm">
          <TextInput
            label={t("Name of Assessor")}
            required
            value={assessment.assessorName || ""}
            disabled={locked}
            error={errors.assessorName}
            onChange={(event) => commit({ assessorName: event.currentTarget.value })}
          />
          <TextInput
            label={t("Name of Advisor")}
            required
            value={assessment.advisorName || ""}
            disabled={locked}
            error={errors.advisorName}
            onChange={(event) => commit({ advisorName: event.currentTarget.value })}
          />
          <TextInput
            label={t("Name of Assessee")}
            required
            value={assessment.assesseeName || ""}
            disabled={locked}
            error={errors.assesseeName}
            onChange={(event) => commit({ assesseeName: event.currentTarget.value })}
          />
          <TextInput
            label={t("Code")}
            value={assessment.assesseeCode || ""}
            disabled={locked}
            onChange={(event) => commit({ assesseeCode: event.currentTarget.value })}
          />
          <Radio.Group
            label={t("Type of Assessee")}
            required
            value={assessment.assesseeType || ""}
            error={errors.assesseeType}
            onChange={(assesseeType) => commit({ assesseeType })}
          >
            {ASSESSEE_TYPES.map((option) => (
              <Radio key={option.value} value={option.value} label={t(option.label)} disabled={locked} />
            ))}
          </Radio.Group>
          <Text size="xs" color="dimmed">
            {t("Advisor = inhouse advisor and agent advisor. Staff = all paid staff under FWD Group, FWD Life, FWDFP, etc.")}
          </Text>
          <Radio.Group
            label={t("Review Type")}
            required
            value={assessment.reviewType || ""}
            error={errors.reviewType}
            onChange={(reviewType) => commit({ reviewType })}
          >
            {REVIEW_TYPES.map((option) => (
              <Radio key={option.value} value={option.value} label={t(option.label)} disabled={locked} />
            ))}
          </Radio.Group>
          {assessment.reviewType === "trigger" ? (
            <TextInput
              label={t("Trigger reason")}
              required
              value={assessment.triggerReason || ""}
              disabled={locked}
              error={errors.triggerReason}
              onChange={(event) => commit({ triggerReason: event.currentTarget.value })}
            />
          ) : null}
          <Radio.Group
            label={t("Status")}
            description={t("Existing client goes to question 7. New client goes to question 8.")}
            required
            value={assessment.clientStatus || ""}
            error={errors.clientStatus}
            onChange={(clientStatus) => commit({ clientStatus })}
          >
            {CLIENT_STATUSES.map((option) => (
              <Radio key={option.value} value={option.value} label={t(option.label)} disabled={locked} />
            ))}
          </Radio.Group>
          {assessment.clientStatus === "existing" ? (
            <>
              <DatePicker
                label={t("Previous Risk Assessment Date")}
                value={toDate(assessment.previousAssessmentDate)}
                disabled={locked}
                error={errors.previousAssessmentDate}
                zIndex={2000}
                onChange={(previousAssessmentDate) => commit({ previousAssessmentDate })}
              />
              <Radio.Group
                label={t("Current Risk Level")}
                required
                value={assessment.previousRiskLevel || ""}
                error={errors.previousRiskLevel}
                onChange={(previousRiskLevel) => commit({ previousRiskLevel })}
              >
                {PREVIOUS_RISK_OPTIONS.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={t(option.label)}
                    disabled={locked}
                  />
                ))}
              </Radio.Group>
              <DatePicker
                label={t("Last EDD Date")}
                description={t("Please conduct EDD if the previous review exceeds 12 months.")}
                value={toDate(assessment.lastEddDate)}
                disabled={locked}
                zIndex={2000}
                onChange={(lastEddDate) => commit({ lastEddDate })}
              />
            </>
          ) : null}
          <Textarea
            label={t("Country in connected")}
            description={t(
              "State all countries related to (1) residential address, (2) corresponding address if applicable."
            )}
            required
            minRows={2}
            value={assessment.countriesConnected || ""}
            disabled={locked}
            error={errors.countriesConnected}
            onChange={(event) => commit({ countriesConnected: event.currentTarget.value })}
          />
          <Radio.Group
            label={t("Highest country risk rating")}
            description={t("Low, Medium, High, or Targeted/Sectoral sanctioned continues to Section 2. Comprehensive sanctioned is Unacceptable Risk and goes to Section 6.")}
            required
            value={assessment.highestCountryRisk || ""}
            error={errors.highestCountryRisk}
            onChange={(highestCountryRisk) => commit({ highestCountryRisk })}
          >
            {COUNTRY_RISK_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value} label={t(option.label)} disabled={locked} />
            ))}
          </Radio.Group>
        </Stack>
      </SectionBlock>

      <SectionBlock
        title="Section 2 – Name Screening"
        open={open["2"]}
        onToggle={() => setOpen((current) => ({ ...current, 2: !current["2"] }))}
        related={related.section2}
      >
        <Stack spacing="sm">
          <Text size="sm" weight={600}>
            {t("Which of the following parties have been go through the Name Screening process?")}
          </Text>
          <Text size="xs" color="dimmed">
            {t("Give detail (name & relationship with client) if name(s) is / are not the same as the client mentioned in question 3.")}
          </Text>
          <CheckList
            rows={SCREENED_PARTIES}
            disabled={locked}
            isChecked={(id) => party(id).checked}
            detail={party("others").detail}
            detailError={errors.screenedPartyDetail}
            onToggle={(id) => {
              const next = SCREENED_PARTIES.map((item) => {
                const current = party(item.id);
                return item.id === id ? { ...current, checked: !current.checked } : current;
              });
              commit({ screenedParties: next });
            }}
            onDetail={(detail) => {
              const next = SCREENED_PARTIES.map((item) => {
                const current = party(item.id);
                return item.id === "others" ? { ...current, detail } : current;
              });
              commit({ screenedParties: next });
            }}
          />
          <FieldError message={errors.screenedParties} />
          <Radio.Group
            label={t('Any "True Hit(s)" from Name Screening process?')}
            required
            value={assessment.trueHit || ""}
            error={errors.trueHit}
            onChange={(trueHit) => commit({ trueHit, trueHitTypes: trueHit === "yes" ? assessment.trueHitTypes : [] })}
          >
            <Radio value="no" label={t("No — Go to Section 3.")} disabled={locked} />
            <Radio value="yes" label={t("Yes — Go to question 11.")} disabled={locked} />
          </Radio.Group>
          {route.showQuestion11 ? (
            <>
              <Text size="sm" weight={600}>
                {t('What is / are the "True Hit(s)" from Name Screening process?')}
              </Text>
              <CheckList
                rows={TRUE_HIT_TYPES}
                disabled={locked}
                isChecked={(id) => (assessment.trueHitTypes || []).includes(id)}
                onToggle={(id) => toggleIn("trueHitTypes", id)}
                onDetail={() => {}}
              />
              <Text size="xs" color="red">
                {t("Terrorist, sanctioned person / OFAC, or cold shoulder order: Unacceptable Risk, go to Section 6.")}
              </Text>
              <Text size="xs" color="orange">
                {t("PEP, connected party, adverse news, sanctioned associate, or state-owned entity: continue to Section 3.")}
              </Text>
              <Text size="xs" color="dimmed">
                {t("Please seek compliance approval if the assessee is applying insurance product only and there is a cold shoulder order by the SFC.")}
              </Text>
              <FieldError message={errors.trueHitTypes} />
            </>
          ) : null}
        </Stack>
      </SectionBlock>

      <SectionBlock
        title="Section 3 – Assessment on Unacceptable Risk"
        open={open["3"]}
        onToggle={() => setOpen((current) => ({ ...current, 3: !current["3"] }))}
        related={related.section3}
      >
        <Stack spacing="sm">
          <Text size="sm">{t("This section is applied to ALL business.")}</Text>
          <Text size="sm" weight={600}>
            {t("Does any of the following factor(s) matched with the individual and /or corporate?")}
          </Text>
          <ChoiceTable
            rows={UNACCEPTABLE_FACTORS.map((factor) => ({ ...factor, na: true }))}
            answers={assessment.unacceptableFactors}
            disabled={locked}
            error={errors["unacceptable-risk factor"]}
            onChange={(id, answer) => setAnswer("unacceptableFactors", id, answer)}
          />
          <Text size="sm">
            {t("Any Yes in question 12")}: {route.question13 === "yes" ? t("Yes — Unacceptable Risk, go to Section 6") : route.question13 === "no" ? t("No — Continue to Section 4") : t("—")}
          </Text>
        </Stack>
      </SectionBlock>

      <SectionBlock
        title="Section 4 – Assessment on Increased Risk – single risk factor"
        open={open["4"]}
        onToggle={() => setOpen((current) => ({ ...current, 4: !current["4"] }))}
        related={related.section4}
      >
        <Stack spacing="md">
          <Text size="sm" weight={600}>
            {t("Which types of business is the assessee applying/ applied? (Please select all apply)")}
          </Text>
          <CheckList
            rows={BUSINESS_TYPES.map((row) => ({ ...row, hint: BUSINESS_HINTS[row.id] }))}
            disabled={locked}
            isChecked={(id) => (assessment.businessTypes || []).includes(id)}
            detail={assessment.businessTypeOther}
            detailError={errors.businessTypeOther}
            onToggle={(id) => toggleIn("businessTypes", id)}
            onDetail={(businessTypeOther) => commit({ businessTypeOther })}
          />
          <FieldError message={errors.businessTypes} />
          {route.show15a ? (
            <>
              <Text size="sm" weight={600}>
                {t("15(a). Does any of the following factor(s) matched with the individual and /or corporate?")}
              </Text>
              <ChoiceTable
                rows={FACTORS_15A}
                answers={assessment.factors15a}
                disabled={locked}
                error={errors["question 15(a) factor"]}
                onChange={(id, answer) => setAnswer("factors15a", id, answer)}
              />
            </>
          ) : null}
          {route.show15b ? (
            <>
              <Text size="sm" weight={600}>
                {t("15(b). Question 15(b) is only applied to assessee who is carrying on investment business.")}
              </Text>
              <ChoiceTable
                rows={FACTORS_15B}
                answers={assessment.factors15b}
                disabled={locked}
                error={errors["question 15(b) factor"]}
                onChange={(id, answer) => setAnswer("factors15b", id, answer)}
              />
            </>
          ) : null}
          {route.show16 ? (
            <>
              <Text size="sm" weight={600}>
                {t("16. Monetary threshold")}
              </Text>
              <ChoiceTable
                rows={MONETARY_ROWS}
                answers={assessment.monetary}
                disabled={locked}
                error={errors["monetary threshold"]}
                onChange={(id, answer) => setAnswer("monetary", id, answer)}
              />
            </>
          ) : null}
        </Stack>
      </SectionBlock>

      <SectionBlock
        title="Section 5 – Assessment on Increased Risk – Dual Risk Factor(s)"
        open={open["5"]}
        onToggle={() => setOpen((current) => ({ ...current, 5: !current["5"] }))}
        related={related.section5}
      >
        <Stack spacing="sm">
          <Text size="sm">
            {t("This section is applied to assessee who is in related to Life insurance business and/or Investment business.")}
          </Text>
          <ChoiceTable
            rows={DUAL_FACTORS}
            answers={assessment.dualFactors}
            disabled={locked}
            error={errors["dual risk factor"]}
            onChange={(id, answer) => setAnswer("dualFactors", id, answer)}
          />
          <Text size="xs" color="dimmed">
            {t("Consider question 20 in conjunction with existing life insurance policies and investment account of the individual.")}
          </Text>
        </Stack>
      </SectionBlock>

      <SectionBlock
        title="Section 6 – Risk profiling and reviewing on result"
        open={open["6"]}
        onToggle={() => setOpen((current) => ({ ...current, 6: !current["6"] }))}
        related={related.section6}
      >
        <Stack spacing="sm">
          <Text size="sm">
            {t("Suggested rating")}: <Text component="span" weight={700}>{t(assessment.suggestedRisk)}</Text>
          </Text>
          <Radio.Group
            label={t("What is the final risk rating?")}
            required
            value={assessment.finalRisk || ""}
            error={errors.finalRisk}
            onChange={(finalRisk) => {
              const next = chooseFinalRisk(assessment, finalRisk);
              onChange(next);
              form?.setFieldValue?.("riskLevel", next.finalRisk);
            }}
          >
            {AML_RISK_LEVELS.map((level) => (
              <Radio
                key={level}
                value={level}
                disabled={locked}
                label={
                  <Box>
                    <Text size="sm">{t(level)}</Text>
                    <Text size="xs" color="dimmed">
                      {t(RATING_HELP[level])}
                    </Text>
                  </Box>
                }
              />
            ))}
          </Radio.Group>
          <Textarea
            label={t("Remarks (e.g., Rationale of manual adjustment on final risk rating)")}
            required={assessment.riskOverridden}
            minRows={4}
            value={assessment.remarks || ""}
            disabled={locked}
            error={errors.remarks}
            onChange={(event) => commit({ remarks: event.currentTarget.value })}
          />
          <Text size="xs">{t(PAPER_NOTES.note1)}</Text>
          <Text size="xs">{t(PAPER_NOTES.note2)}</Text>
        </Stack>
      </SectionBlock>
    </Box>
  );
};

export default FwdAmlAssessmentForm;
